import { env } from './env';

/**
 * Session Management Configuration
 * Centralized configuration for inactivity-based session management
 * Now supports runtime configuration via window.__ENV__
 */

export interface SessionConfig {
  /**
   * Inactivity timeout in milliseconds
   * User will be logged out after this period of inactivity
   * @default 5 minutes (300000ms)
   */
  inactivityTimeout: number;

  /**
   * Warning time before timeout in milliseconds
   * Warning modal will appear this many milliseconds before logout
   * @default 30 seconds (30000ms)
   */
  warningBeforeTimeout: number;

  /**
   * Enable session monitoring on app start
   * @default true
   */
  autoStartMonitoring: boolean;

  /**
   * Enable debug logging for session management
   * @default false in production, true in development
   */
  debugMode: boolean;
}

/**
 * Default session configuration
 * Can be overridden via environment variables or runtime configuration
 * Now uses runtime environment configuration for flexible deployment
 */
export const defaultSessionConfig: SessionConfig = {
  // 5 minutes inactivity timeout (configurable via runtime or build-time)
  inactivityTimeout: env.SESSION_INACTIVITY_TIMEOUT,

  // 30 seconds warning before logout (configurable via runtime or build-time)
  warningBeforeTimeout: env.SESSION_WARNING_TIME,

  // Auto-start monitoring when user logs in
  autoStartMonitoring: true,

  // Debug mode based on environment
  debugMode: env.IS_DEV,
};

/**
 * Preset configurations for different use cases
 */
export const sessionPresets = {
  /**
   * Quick testing preset - 1 minute timeout
   */
  testing: {
    inactivityTimeout: 1 * 60 * 1000,
    warningBeforeTimeout: 15 * 1000,
    autoStartMonitoring: true,
    debugMode: true,
  } as SessionConfig,

  /**
   * Development preset - 10 minutes timeout
   */
  development: {
    inactivityTimeout: 10 * 60 * 1000,
    warningBeforeTimeout: 30 * 1000,
    autoStartMonitoring: true,
    debugMode: true,
  } as SessionConfig,

  /**
   * Standard preset - 5 minutes timeout (default)
   */
  standard: {
    inactivityTimeout: 5 * 60 * 1000,
    warningBeforeTimeout: 30 * 1000,
    autoStartMonitoring: true,
    debugMode: false,
  } as SessionConfig,

  /**
   * Extended preset - 12 hours timeout
   */
  extended: {
    inactivityTimeout: 12 * 60 * 60 * 1000,
    warningBeforeTimeout: 60 * 1000,
    autoStartMonitoring: true,
    debugMode: false,
  } as SessionConfig,

  /**
   * Strict preset - 2 minutes timeout
   */
  strict: {
    inactivityTimeout: 2 * 60 * 1000,
    warningBeforeTimeout: 30 * 1000,
    autoStartMonitoring: true,
    debugMode: false,
  } as SessionConfig,
};

/**
 * Get session configuration based on environment
 * Now supports runtime configuration via window.__ENV__
 */
export const getSessionConfig = (): SessionConfig => {
  const preset = env.SESSION_PRESET as keyof typeof sessionPresets;

  if (preset && sessionPresets[preset]) {
    return sessionPresets[preset];
  }

  return defaultSessionConfig;
};
